import { CopyOutlined } from "@ant-design/icons";
import { Dropdown, MenuProps } from "antd";
import { FC, SyntheticEvent, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildRoutePath } from "tm-shared/utils";

import FlexContainer from "@/components/FlexContainer";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppSelector } from "@/hooks/redux";
import { QrModal } from "@/pages/WorkPlan/components/QRModal";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";

import {
    StyledMBAboutMarketButton,
    StyledMBCopyButton,
    StyledMBGeoPin,
    StyledMBMarketMenuController,
    StyledMBQRIcon,
} from "./Styled";

interface MarketActionsBarProps {
    marketId: string;
    marketName: string;
    geo: string | null;
    address: string;
    menu?: MenuProps;
    objToCopy: { [key: string]: unknown };
}

export const MarketActionsBar: FC<MarketActionsBarProps> = ({
    marketId,
    menu,
    geo,
    address,
    marketName,
    objToCopy,
}) => {
    const { role } = useAppSelector((s) => s.user);
    const [qrModalOpen, setQRModalOpen] = useState<boolean>(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const navigate = useNavigate();
    const isManagerNow = isManagerRole(role);

    const handleOpenPoint = (e: SyntheticEvent) => {
        e.preventDefault();
        const currentAddress = geo || address;
        window.open(
            `https://www.google.com/maps/place/${currentAddress?.replaceAll(
                " ",
                "+",
            )}`,
        );
    };

    const handleCopyMarket = () => {
        copyObjectToClipboard(objToCopy);
    };

    const handleAboutMarket = useCallback(
        () =>
            navigate(
                buildRoutePath(ROUTES.WorkPlanMarketDetails, {
                    marketId,
                }),
            ),
        [marketId, navigate],
    );

    const patchedMenu = useMemo(() => {
        if (!menu) return undefined;
        return {
            ...menu,
            items: menu.items?.map((item) => {
                if (
                    !item ||
                    !("closeDropdown" in item) ||
                    !item.closeDropdown
                ) {
                    return item;
                }
                const typedItem = item as typeof item & {
                    onClick?: () => void;
                };
                return {
                    ...typedItem,
                    onClick: () => {
                        typedItem.onClick?.();
                        setIsDropdownOpen(false);
                    },
                };
            }),
        };
    }, [menu]);

    return (
        <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            width="100%"
        >
            <FlexContainer
                justifyContent="flex-end"
                alignItems="center"
                gap="12px"
            >
                <StyledMBAboutMarketButton onClick={handleAboutMarket}>
                    Про ТТ
                </StyledMBAboutMarketButton>
                {isManagerNow && (
                    <StyledMBQRIcon onClick={() => setQRModalOpen(true)} />
                )}
                <StyledMBGeoPin onClick={handleOpenPoint} />

                <StyledMBCopyButton
                    shape="circle"
                    icon={<CopyOutlined />}
                    onClick={handleCopyMarket}
                />
            </FlexContainer>

            {patchedMenu && patchedMenu?.items?.length > 0 && (
                <Dropdown
                    open={isDropdownOpen}
                    onOpenChange={(nextOpen, info) => {
                        if (info?.source === "trigger" || nextOpen) {
                            setIsDropdownOpen(nextOpen);
                        }
                    }}
                    menu={patchedMenu}
                    trigger={["click"]}
                >
                    <StyledMBMarketMenuController />
                </Dropdown>
            )}

            {isManagerNow && (
                <QrModal
                    open={qrModalOpen}
                    marketId={marketId}
                    marketName={marketName}
                    onClose={() => setQRModalOpen(false)}
                />
            )}
        </FlexContainer>
    );
};
